import { useQuery } from "@apollo/client";
import _ from "lodash";
import { ReactElement } from "react";

import {
  CampaignContactFollowUpImportArgs,
  CampaignSortBy,
  FollowUpFilterInput,
  ListCampaignsQuery,
  ListCampaignsQueryVariables,
} from "~frontend/__generated__/graphql";
import { LIST_CAMPAIGNS } from "~frontend/graphql/campaigns";
import { useSubaccount } from "~frontend/providers/SubaccountProvider";

import useTagName from "./useTagName";

export const FOLLOW_UP_FILTER_LABELS: {
  [k in FollowUpFilterInput]: string;
} = {
  [FollowUpFilterInput.All]: "All Contacts",
  [FollowUpFilterInput.Unmessaged]: "Contacts who were not messaged",
  [FollowUpFilterInput.Filtered]: "Contacts whose messages were filtered",
  [FollowUpFilterInput.Errored]: "Contacts whose messages errored",
  [FollowUpFilterInput.Donated]: "Contacts who donated",
  [FollowUpFilterInput.Clicked]: "Contacts who clicked a link",
  [FollowUpFilterInput.ClickedWithoutDonating]:
    "Contacts who clicked a link but did not donate",
  [FollowUpFilterInput.Replied]: "Contacts who replied",
  [FollowUpFilterInput.DidNotReply]: "Contacts who did not reply",
  [FollowUpFilterInput.Tagged]: "Contacts who were tagged with...",
  [FollowUpFilterInput.ScriptVariant]:
    "Contacts who were sent initial variant...",
};

export const FOLLOW_UP_FILTERS = Object.keys(
  FOLLOW_UP_FILTER_LABELS
) as FollowUpFilterInput[];

function TagFilterSummary({
  jobArgs,
}: {
  jobArgs: CampaignContactFollowUpImportArgs;
}): ReactElement | null {
  const { getTagName, state } = useTagName();

  if (state === "loading") {
    return null;
  }

  if (state === "error") {
    return <>Error loading tags</>;
  }

  return (
    <>
      {(jobArgs.followUpTagIds || [])
        .map(id => getTagName(id) ?? "Deleted Tag")
        .join(", ")}
    </>
  );
}

function ScriptVariantFilterSummary({
  jobArgs,
}: {
  jobArgs: CampaignContactFollowUpImportArgs;
}): ReactElement | null {
  const { subaccount } = useSubaccount()!;

  const campaignData = useQuery<
    ListCampaignsQuery,
    ListCampaignsQueryVariables
  >(LIST_CAMPAIGNS, {
    variables: {
      input: {
        filter: { idIn: jobArgs.followUpCampaignIds },
        sort: CampaignSortBy.CreatedAtAsc,
        subaccountId: subaccount.id,
        limit: jobArgs.followUpCampaignIds.length,
      },
    },
  });

  if (campaignData.loading) {
    return null;
  }

  if (campaignData.error) {
    return <>Error loading script variants</>;
  }

  const campaigns = campaignData.data!.listCampaigns.campaigns;

  const scriptVariantDescriptions: Record<string, string> = {};
  campaigns.forEach(campaign => {
    let orderInCampaign = 0;

    for (const variant of _.sortBy(campaign.scriptVariants, [
      variant => (variant.deletedAt ? 1 : 0),
      variant => variant.order,
    ])) {
      const variantDescription = variant.deletedAt
        ? "Deleted Variant"
        : `Variant ${orderInCampaign + 1}`;

      if (campaigns.length > 1) {
        scriptVariantDescriptions[variant.id] =
          `${campaign.name} - ${variantDescription}`;
      } else {
        scriptVariantDescriptions[variant.id] = variantDescription;
      }

      orderInCampaign++;
    }
  });

  return (
    <>
      {(jobArgs.followUpScriptVariantIds || [])
        .map(id => scriptVariantDescriptions[id])
        .join(", ")}
    </>
  );
}

export default function FollowUpCampaignFilterSummary({
  jobArgs,
}: {
  jobArgs: CampaignContactFollowUpImportArgs;
}): ReactElement | null {
  switch (jobArgs.followUpFilter) {
    case FollowUpFilterInput.All:
      return null;
    case FollowUpFilterInput.Unmessaged:
    case FollowUpFilterInput.Filtered:
    case FollowUpFilterInput.Errored:
    case FollowUpFilterInput.Donated:
    case FollowUpFilterInput.Clicked:
    case FollowUpFilterInput.ClickedWithoutDonating:
    case FollowUpFilterInput.Replied:
    case FollowUpFilterInput.DidNotReply:
      return <>(filter: {FOLLOW_UP_FILTER_LABELS[jobArgs.followUpFilter]})</>;
    case FollowUpFilterInput.Tagged:
      return (
        <>
          (filtered by tags: <TagFilterSummary jobArgs={jobArgs} />)
        </>
      );
    case FollowUpFilterInput.ScriptVariant:
      return (
        <>
          (filtered by script variants:{" "}
          <ScriptVariantFilterSummary jobArgs={jobArgs} />)
        </>
      );
  }
}
